import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Users, Package, FileText, LayoutDashboard, Menu, X, LogOut, UserCog, Lock, Receipt, Sun, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const OperationsLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Admin has access to all sections
  // Staff only has access to delivery notes
  const navItems = [
    { path: '/operations', label: 'Dashboard', icon: LayoutDashboard, exact: true, roles: ['admin', 'staff'] },
    {
      path: '/operations/delivery-notes', label: 'Delivery Notes', icon: FileText, roles: ['admin', 'staff'],
      children: [
        { path: '/operations/delivery-notes/today',      label: 'Today',      icon: Sun },
        { path: '/operations/delivery-notes/historical', label: 'Historical', icon: Archive },
      ],
    },
    { path: '/operations/invoicing', label: 'Invoicing', icon: Receipt, roles: ['admin'] },
    { divider: true, label: 'Admin Section', roles: ['admin'] },
    { path: '/operations/customers', label: 'Manage Customers', icon: Users, roles: ['admin'] },
    // Step 5: 'Customer Items' is the everyday workflow for adding/editing
    // items per customer. 'Item Catalogue' is the master list (rare
    // power-user view — global rename, deactivation across all customers).
    { path: '/operations/customer-items', label: 'Customer Items', icon: Package, roles: ['admin'] },
    { path: '/operations/items', label: 'Item Catalogue', icon: Package, roles: ['admin'] },
    { path: '/operations/staff', label: 'Staff Accounts', icon: UserCog, roles: ['admin'] },
  ];

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-20">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-24 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-[#1F2D3D] text-white rounded-lg shadow-lg"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#1F2D3D] text-white transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } pt-24 lg:pt-4 flex flex-col`}
        >
          <div className="p-4 flex-1">
            <div className="mb-6">
              <h2 className="text-xl font-bold" style={{ fontFamily: '"Cinzel", serif' }}>
                Operations
              </h2>
              <p className="text-white/60 text-sm mt-1">
                {user?.name} • {user?.role === 'admin' ? 'Admin' : 'Staff'}
              </p>
            </div>
            <nav className="space-y-2">
              {visibleNavItems.map((item, index) => {
                if (item.divider) {
                  return (
                    <div key={`divider-${index}`} className="pt-4 pb-2">
                      <div className="border-t border-white/20 mb-3"></div>
                      <p className="text-xs uppercase text-white/60 font-semibold px-4">{item.label}</p>
                    </div>
                  );
                }
                
                const Icon = item.icon;
                const active = isActive(item.path, item.exact);
                const children = item.children || [];
                // Show children only when the parent group is active (i.e.
                // user is on the parent path or one of its children).
                const showChildren = children.length > 0 && active;
                return (
                  <div key={item.path}>
                    <Link
                      to={children[0]?.path || item.path}
                      onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                      className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                        active
                          ? 'bg-white text-[#1F2D3D]'
                          : 'text-white/80 hover:bg-white/10'
                      }`}
                    >
                      <Icon size={20} className="mr-3" />
                      {item.label}
                    </Link>
                    {showChildren && (
                      <div className="ml-6 mt-1 space-y-1">
                        {children.map(c => {
                          const CIcon = c.icon;
                          const cActive = location.pathname.startsWith(c.path);
                          return (
                            <Link
                              key={c.path}
                              to={c.path}
                              onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                              className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                                cActive
                                  ? 'bg-white/90 text-[#1F2D3D]'
                                  : 'text-white/70 hover:bg-white/10'
                              }`}
                            >
                              <CIcon size={14} className="mr-2" />
                              {c.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Sidebar Footer with Logout and Change Password */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <Link
              to="/operations/change-password"
              className="flex items-center px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 transition-colors"
            >
              <Lock size={20} className="mr-3" />
              Change Password
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 rounded-lg text-white/80 hover:bg-red-500/20 transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default OperationsLayout;
